import { createTheme, type Theme } from '@mui/material/styles';
import { type PaletteOptions } from '@mui/material/styles/createPalette';
import { useState, useMemo, createContext, useContext } from 'react';

// color design tokens export - based on backend implementation
export const tokens = (mode: string) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          500: "#141b2d",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#040509",
          200: "#080b12",
          300: "#0c101b",
          400: "#f2f0f0", // manually changed
          500: "#141b2d",
          600: "#1F2A40",
          700: "#727681",
          800: "#a1a4ab",
          900: "#d0d1d5",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
      }),
});

// Theme color definitions
export const themeColors = {
  brand: {
    dark: '#6383F1',
    main: '#4F6AF8',
    light: '#F1F5FE',
  },
  light: {
    primary: {
      main: '#4F6AF8',
      light: '#8DA1FA',
      dark: '#3854C4',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4FCEF8',
      light: '#80DEFC',
      dark: '#07A3CF',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F8F9FE',
      paper: '#ffffff',
    },
    text: {
      primary: '#1D2542',
      secondary: '#5F6A8A',
    },
    error: {
      main: '#F85149',
      light: '#FC8983',
      dark: '#D02C26',
    },
    warning: {
      main: '#F8A951',
      light: '#FCC183',
      dark: '#D07D26',
    },
    success: {
      main: '#4CDCA2',
      light: '#8FECC9',
      dark: '#27A46D',
    },
    grey: {
      50: '#F9FAFA',
      100: '#F0F1F2',
      200: '#E4E6E8',
      300: '#D2D4D8',
      400: '#A9ADB6',
      500: '#797E89',
      600: '#5F6A8A',
      700: '#454D61',
      800: '#2C3247',
      900: '#1D2542',
    },
    divider: 'rgba(93, 106, 137, 0.12)',
  },
  dark: {
    primary: {
      main: '#4F6AF8',
      light: '#8DA1FA',
      dark: '#3854C4',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4FCEF8',
      light: '#80DEFC',
      dark: '#07A3CF',
      contrastText: '#ffffff',
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: '#E0E2E7',
      secondary: '#A9ADB6',
    },
    error: {
      main: '#F85149',
      light: '#FC8983',
      dark: '#D02C26',
    },
    warning: {
      main: '#F8A951',
      light: '#FCC183',
      dark: '#D07D26',
    },
    success: {
      main: '#4CDCA2',
      light: '#8FECC9',
      dark: '#27A46D',
    },
    grey: {
      900: '#E0E2E7',
      800: '#C8CCD3',
      700: '#A9ADB6',
      600: '#797E89',
      500: '#5F6A8A',
      400: '#454D61',
      300: '#2C3247',
      200: '#1D2542',
      100: '#171D32',
      50: '#111627',
    },
    divider: 'rgba(229, 231, 235, 0.12)',
  },
  midnight: {
    primary: {
      main: '#6383F1',
      light: '#94B0FD',
      dark: '#3854C4',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9B72F2',
      light: '#BE9DF6',
      dark: '#7445E8',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0B0F19',
      paper: '#111627',
    },
    text: {
      primary: '#E0E2E7',
      secondary: '#A9ADB6',
    },
    error: {
      main: '#F85149',
      light: '#FC8983',
      dark: '#D02C26',
    },
    warning: {
      main: '#F8A951',
      light: '#FCC183',
      dark: '#D07D26',
    },
    success: {
      main: '#4CDCA2',
      light: '#8FECC9',
      dark: '#27A46D',
    },
    grey: {
      900: '#E0E2E7',
      800: '#C8CCD3',
      700: '#A9ADB6',
      600: '#797E89',
      500: '#5F6A8A',
      400: '#454D61',
      300: '#2C3247',
      200: '#1D2542',
      100: '#171D32',
      50: '#111627',
    },
    divider: 'rgba(229, 231, 235, 0.12)',
  },
  sunset: {
    primary: {
      main: '#FF6B6B',
      light: '#FF9E9E',
      dark: '#C73E3E',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FFD166',
      light: '#FFDF94',
      dark: '#D6A83B',
      contrastText: '#1D2542',
    },
    background: {
      default: '#1F1D2C',
      paper: '#272636',
    },
    text: {
      primary: '#E0E2E7',
      secondary: '#A9ADB6',
    },
    error: {
      main: '#F85149',
      light: '#FC8983',
      dark: '#D02C26',
    },
    warning: {
      main: '#F8A951',
      light: '#FCC183',
      dark: '#D07D26',
    },
    success: {
      main: '#4CDCA2',
      light: '#8FECC9',
      dark: '#27A46D',
    },
    grey: {
      900: '#E0E2E7',
      800: '#C8CCD3',
      700: '#A9ADB6',
      600: '#797E89',
      500: '#5F6A8A',
      400: '#454D61',
      300: '#2C3247',
      200: '#1D2542',
      100: '#282537',
      50: '#1F1D2C',
    },
    divider: 'rgba(229, 231, 235, 0.12)',
  },
  // New Ocean theme with blue tones
  ocean: {
    primary: {
      main: '#0277BD',
      light: '#58A5F0',
      dark: '#004C8C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#26C6DA',
      light: '#6FF9FF',
      dark: '#0095A8',
      contrastText: '#000000',
    },
    background: {
      default: '#102027',
      paper: '#182731',
    },
    text: {
      primary: '#E1F5FE',
      secondary: '#B3E5FC',
    },
    error: {
      main: '#F85149',
      light: '#FC8983',
      dark: '#D02C26',
    },
    warning: {
      main: '#FDD835',
      light: '#FFFF6B',
      dark: '#C6A700',
    },
    success: {
      main: '#00BFA5',
      light: '#5DF2D6',
      dark: '#008E76',
    },
    grey: {
      900: '#E1F5FE',
      800: '#B3E5FC',
      700: '#81D4FA',
      600: '#4FC3F7',
      500: '#29B6F6',
      400: '#039BE5',
      300: '#0288D1',
      200: '#0277BD',
      100: '#01579B',
      50: '#014666',
    },
    divider: 'rgba(225, 245, 254, 0.12)',
  },
  // New Forest theme with green tones
  forest: {
    primary: {
      main: '#2E7D32',
      light: '#60AD5E',
      dark: '#005005',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#66BB6A',
      light: '#98EE99',
      dark: '#338A3E',
      contrastText: '#000000',
    },
    background: {
      default: '#0A1F0A',
      paper: '#132713',
    },
    text: {
      primary: '#E8F5E9',
      secondary: '#C8E6C9',
    },
    error: {
      main: '#F85149',
      light: '#FC8983',
      dark: '#D02C26',
    },
    warning: {
      main: '#FFB300',
      light: '#FFE54C',
      dark: '#C68400',
    },
    success: {
      main: '#558B2F',
      light: '#85BB5C',
      dark: '#255D00',
    },
    grey: {
      900: '#E8F5E9',
      800: '#C8E6C9',
      700: '#A5D6A7',
      600: '#81C784',
      500: '#66BB6A',
      400: '#4CAF50',
      300: '#43A047',
      200: '#388E3C',
      100: '#2E7D32',
      50: '#1B5E20',
    },
    divider: 'rgba(232, 245, 233, 0.12)',
  },
};

export type ThemeVariant = 'light' | 'dark' | 'midnight' | 'sunset' | 'ocean' | 'forest';

// Create theme with given variant
export const createAppTheme = (variant: ThemeVariant): Theme => {
  // Simply use the palette for the specified variant
  const paletteBase = themeColors[variant];
  
  // Determine if this is a light or dark mode theme - affects some component styling
  // Light theme is the only light mode palette
  const mode = variant === 'light' ? 'light' : 'dark';
  
  return createTheme({
    palette: {
      mode,
      ...paletteBase,
      action: {
        active: paletteBase.text.primary,
        hover: mode === 'light' 
          ? 'rgba(0, 0, 0, 0.04)' 
          : 'rgba(255, 255, 255, 0.08)',
        selected: mode === 'light' 
          ? 'rgba(0, 0, 0, 0.08)' 
          : 'rgba(255, 255, 255, 0.16)',
        disabled: mode === 'light' 
          ? 'rgba(0, 0, 0, 0.26)' 
          : 'rgba(255, 255, 255, 0.3)',
        disabledBackground: mode === 'light' 
          ? 'rgba(0, 0, 0, 0.12)' 
          : 'rgba(255, 255, 255, 0.12)',
      },
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      subtitle1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.5,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 8,
            padding: '8px 16px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          sizeSmall: {
            padding: '6px 12px',
          },
          sizeLarge: {
            padding: '10px 20px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'light' 
              ? '0px 4px 20px rgba(0, 0, 0, 0.05)' 
              : '0px 4px 20px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: paletteBase.text.primary,
            '&:hover': {
              backgroundColor: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.04)' 
                : 'rgba(255, 255, 255, 0.08)',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: paletteBase.background.paper,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: paletteBase.background.paper,
            color: paletteBase.text.primary,
            boxShadow: mode === 'light' 
              ? '0px 1px 3px rgba(0, 0, 0, 0.1)' 
              : '0px 1px 3px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'light' 
              ? 'rgba(0, 0, 0, 0.75)' 
              : 'rgba(255, 255, 255, 0.75)',
            color: mode === 'light' ? '#fff' : '#000',
            fontSize: '0.75rem',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
    },
  });
};

// Default theme
export const theme = createAppTheme('light');

// Theme context
interface ThemeContextType {
  theme: ThemeVariant;
  changeTheme: (theme: ThemeVariant) => void;
  isLightMode: boolean;
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  changeTheme: () => {},
  isLightMode: true,
});

// Custom hook for using theme context
export const useThemeContext = () => useContext(ThemeContext);

// Theme provider hook
export const useThemeProvider = () => {
  // Use localStorage for theme persistence if available
  const getStoredTheme = (): ThemeVariant => {
    try {
      const storedTheme = localStorage.getItem('mattoboard-theme');
      if (storedTheme) {
        const theme = storedTheme as ThemeVariant;
        if (['light', 'dark', 'midnight', 'sunset', 'ocean', 'forest'].includes(theme)) {
          return theme as ThemeVariant;
        }
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    }
    // Default to light theme
    return 'light';
  };

  // Initialize with stored theme or default to light
  const [currentTheme, setCurrentTheme] = useState<ThemeVariant>(getStoredTheme());
  
  // Calculate if we're in light mode (only the 'light' theme is light mode)
  const isLightMode = currentTheme === 'light';

  // Update localStorage when theme changes
  const saveTheme = (theme: ThemeVariant) => {
    try {
      localStorage.setItem('mattoboard-theme', theme);
    } catch (error) {
      console.error('Error saving theme to localStorage:', error);
    }
  };

  // Create theme context value
  const themeContextValue = useMemo(
    () => ({
      theme: currentTheme,
      isLightMode,
      changeTheme: (newTheme: ThemeVariant) => {
        setCurrentTheme(newTheme);
        saveTheme(newTheme);
      },
    }),
    [currentTheme, isLightMode],
  );

  // Generate the actual MUI theme
  const theme = useMemo(() => createAppTheme(currentTheme), [currentTheme]);

  return {
    theme,
    colorMode: themeContextValue,
  };
};
